/**
 * Sync group identity and deterministic seed helpers for multi-room playback.
 */

/** Resolve the sync group id from device settings (defaults to locationId). */
export function resolveSyncGroupId(
  settings: unknown,
  locationId: string | null | undefined,
): string | null {
  const custom = (settings as { syncGroupId?: unknown } | null)?.syncGroupId;
  if (typeof custom === "string" && custom.trim().length > 0) {
    return custom.trim();
  }
  return locationId ?? null;
}

/** Base deterministic seed for a sync group on a given calendar day. */
export function computeDeterministicSeed(
  syncGroupId: string,
  at: Date = new Date(),
): string {
  const day = at.toISOString().slice(0, 10);
  return `${syncGroupId}:${day}`;
}

export interface SyncSegment {
  channelType: "music" | "ads" | "announcements" | "video";
  itemId: string;
  startsAt: string;
  durationSec: number;
}

/**
 * Tolerance past a segment's computed end before it stops being "active".
 * Segment durations are nominal (default-duration guesses, encoder drift), so
 * a hard cutoff would flap at every boundary; but an outrun timeline must
 * eventually answer null (see activeSegmentAt).
 */
export const SEGMENT_END_TOLERANCE_MS = 1_000;

/** True once `now` is past the segment's end (plus tolerance). */
export function segmentEnded(
  segment: SyncSegment,
  now: Date = new Date(),
): boolean {
  const endMs =
    new Date(segment.startsAt).getTime() + segment.durationSec * 1000;
  return now.getTime() >= endMs + SEGMENT_END_TOLERANCE_MS;
}

/**
 * Find the segment active at `now`: the last segment whose startsAt <= now,
 * unless that segment has already ENDED. The end bound is load-bearing: a
 * timeline outrun by real time (sync outage, suspend/resume, short items)
 * used to pin its dead tail segment forever — alignment then "seeked" tracks
 * to their end and the drift loop measured a huge phantom drift against an
 * item that stopped being scheduled long ago. Past the end of the timeline
 * the correct answer is null (degrade to the local rotation), not the tail.
 */
export function activeSegmentAt(
  timeline: readonly SyncSegment[],
  now: Date = new Date(),
): { segment: SyncSegment; index: number } | null {
  if (timeline.length === 0) return null;
  const ts = now.getTime();
  let active: { segment: SyncSegment; index: number } | null = null;
  for (let i = 0; i < timeline.length; i += 1) {
    const seg = timeline[i]!;
    if (new Date(seg.startsAt).getTime() <= ts) {
      active = { segment: seg, index: i };
    } else {
      break;
    }
  }
  if (active && segmentEnded(active.segment, now)) return null;
  return active;
}

/** Seconds into the active segment (0 when before the first segment). */
export function positionInSegment(
  segment: SyncSegment,
  now: Date = new Date(),
): number {
  const start = new Date(segment.startsAt).getTime();
  const elapsed = (now.getTime() - start) / 1000;
  return Math.max(0, Math.min(elapsed, segment.durationSec));
}

/**
 * Expected playback position within the active segment vs actual (positive =
 * ahead). An ended segment has no meaningful expectation — returns 0 (no
 * correction) rather than a full-duration phantom drift that would hard-seek
 * the on-air track to its end.
 */
export function driftSec(
  segment: SyncSegment,
  actualPositionSec: number,
  now: Date = new Date(),
): number {
  if (segmentEnded(segment, now)) return 0;
  return actualPositionSec - positionInSegment(segment, now);
}

// --- operator seek rebase ----------------------------------------------------

/**
 * A deliberate operator seek, recorded so drift correction measures against
 * WHERE THE OPERATOR PUT PLAYBACK rather than against the server's projected
 * group timeline.
 *
 * Without this, a manual seek is indistinguishable from clock drift: the seek
 * offset (tens of seconds) dwarfs `hardThresholdSec` (2s), so the very next
 * drift tick hard-seeks playback back and the operator watches their seek
 * silently undo itself a couple of seconds later.
 *
 * The rebase is derived IDENTICALLY on every device in a sync group, because
 * the server fans one seek command out to the whole group. So the group moves
 * together and stays mutually aligned — it is realigned to a new baseline, not
 * unaligned.
 */
export interface SeekRebase {
  /** Only valid while this exact item is still on air. */
  itemId: string;
  /** Position the operator asked for, in seconds. */
  positionSec: number;
  /** Corrected wall clock (ms) when the seek was applied. */
  atMs: number;
}

/**
 * A rebase cannot outlive its own playback. The hooks clear it whenever an
 * entry (re)starts, but a missed clear used to strand it forever: the match is
 * on itemId with no expiry, so the same track returning to air (rotation loop,
 * previous then next) measured a fresh play against a minutes-old position and
 * hard-seeked on every tick. This bound makes that unreachable by construction.
 */
export const SEEK_REBASE_MAX_AGE_SEC = 2 * 60 * 60;

/**
 * Drift measured against a rebase instead of the timeline: actual − expected,
 * where expected advances in real time from the seek point.
 *
 * Returns null when the rebase does not apply (absent, different item, a clock
 * that has gone backwards, or one older than any real track), so the caller
 * falls back to the timeline.
 */
export function rebasedDriftSec(
  rebase: SeekRebase | null | undefined,
  itemId: string | null | undefined,
  positionSec: number,
  nowMs: number,
): number | null {
  if (!rebase || !itemId || rebase.itemId !== itemId) return null;
  if (!Number.isFinite(positionSec) || !Number.isFinite(nowMs)) return null;
  const elapsedSec = (nowMs - rebase.atMs) / 1000;
  if (elapsedSec < 0) return null;
  if (elapsedSec > SEEK_REBASE_MAX_AGE_SEC) return null;
  return positionSec - (rebase.positionSec + elapsedSec);
}
